// client/src/components/ui/QueryErrorState.tsx
// 868mbzt38 — D1 path.
//
// 71 usages across the app pass `error={query.error}`, where
// `query.error: Error | null`. The contract is:
//   * `error === null` → "errors.queryFailed" (genérico, igual que antes).
//   * `error instanceof RateLimitedError` → "errors.http.tooManyRequests"
//     con el sufijo `(Ns)` derivado de `retryAfterMs`.
//   * `error instanceof ApiRequestError && status === 403 && code === "COMPANY_ACCESS_DENIED"`
//     → pantalla de "no company assigned".
//   * otro Error → "errors.queryFailed".
//
// El `ownerUnitGuard` pasa el `loadError` real (sin envolver en null); la
// guardia de la unidad sigue distinguiendo RateLimitedError con
// `instanceof` y el resto se muestra genérico.

import { useIntl } from 'react-intl';
import { AlertCircle } from 'lucide-react';
import { ApiRequestError, RateLimitedError } from '@/lib/queryClient';
import { Button } from './button';

interface QueryErrorStateProps {
  error: Error | null;
  onRetry?: () => void;
  // 868mbzt38 D1 — el guard dueño del retry desactiva el botón mientras
  // la petición está en vuelo, de modo que el aviso no parpadea entre
  // "Retry" y "Skeleton".
  isRetrying?: boolean;
}

export function QueryErrorState({
  error,
  onRetry,
  isRetrying = false,
}: QueryErrorStateProps) {
  const intl = useIntl();

  const isCompanyAccessDenied =
    error instanceof ApiRequestError &&
    error.status === 403 &&
    error.code === 'COMPANY_ACCESS_DENIED';

  if (isCompanyAccessDenied) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-10 h-10 text-muted-foreground mb-4" />
        <h3 className="font-semibold">{intl.formatMessage({ id: 'errors.noCompanyAssigned.title' })}</h3>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          {intl.formatMessage({ id: 'errors.noCompanyAssigned.description' })}
        </p>
      </div>
    );
  }

  // 429 ⇒ toast genérico + segundos. `error` se mantiene real (no null)
  // para que el resto del contrato no se rompa.
  if (error instanceof RateLimitedError) {
    const base = intl.formatMessage({ id: 'errors.http.tooManyRequests' });
    const seconds =
      Number.isFinite(error.retryAfterMs) && error.retryAfterMs > 0
        ? ` (${Math.ceil(error.retryAfterMs / 1000)}s)`
        : '';
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-10 h-10 text-destructive mb-4" />
        <p className="text-sm text-muted-foreground mb-4">
          {base}
          {seconds}
        </p>
        {onRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRetry}
            disabled={isRetrying}
          >
            {intl.formatMessage({ id: 'common.actions.retry' })}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <AlertCircle className="w-10 h-10 text-destructive mb-4" />
      <p className="text-sm text-muted-foreground mb-4">
        {intl.formatMessage({ id: 'errors.queryFailed' })}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} disabled={isRetrying}>
          {intl.formatMessage({ id: 'common.actions.retry' })}
        </Button>
      )}
    </div>
  );
}
